import { useState, useRef } from 'react';
import { STAGING_AUTH } from '../../config/env';
import { Photo } from 'components';
import { fetchRequests } from 'https/requests';
import { useUser } from 'hooks';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { show } from 'providers/toastSlice';
import { updateUserInfo } from 'providers/userSlice';
import { maxFileSize, validateGhanaCard } from 'components/misc/functions';
import { HugeiconsIcon } from '@hugeicons/react';
import { ShieldUserIcon, InformationCircleIcon, CheckmarkCircle01Icon, Cancel01Icon } from '@hugeicons/core-free-icons';

const PREFIX = 'GHA-';

function formatCardNumber(raw) {
    // Always start with prefix; extract only digits from the rest
    const after = raw.startsWith(PREFIX) ? raw.slice(PREFIX.length) : raw;
    const digits = after.replace(/\D/g, '').slice(0, 10);
    if (digits.length <= 9) return PREFIX + digits;
    return PREFIX + digits.slice(0, 9) + '-' + digits.slice(9);
}

function GCardInfo({ idNumber }) {
    const { accessToken } = useUser();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [cardNumber, setCardNumber] = useState(idNumber ? formatCardNumber(idNumber) : PREFIX);
    const [frontPhoto, setFrontPhoto] = useState('');
    const [backPhoto, setBackPhoto] = useState('');
    const [selfie, setSelfie] = useState('');
    const [processing, setProcessing] = useState(false);
    const inputRef = useRef(null);

    const isValid = validateGhanaCard(cardNumber);
    const hasInput = cardNumber.length > PREFIX.length;

    const handleCardChange = (e) => {
        const next = formatCardNumber(e.target.value);
        setCardNumber(next);
        // Restore cursor to end on next tick (masking shifts characters)
        requestAnimationFrame(() => {
            if (inputRef.current) {
                inputRef.current.selectionStart = next.length;
                inputRef.current.selectionEnd = next.length;
            }
        });
    };

    const handleKeyDown = (e) => {
        const pos = inputRef.current?.selectionStart ?? 0;
        // Prevent backspace/delete from eating into the "GHA-" prefix
        if ((e.key === 'Backspace' || e.key === 'Delete') && pos <= PREFIX.length) {
            e.preventDefault();
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setProcessing(true);

        if (!validateGhanaCard(cardNumber)) {
            dispatch(show({ state: true, message: 'Invalid GhanaCard number. Format: GHA-123456789-0', type: 'error' }));
            setProcessing(false);
            return;
        }

        if (selfie.size > maxFileSize) {
            dispatch(show({ state: true, message: 'Selfie file size is too large. Maximum is 1MB', type: 'error' }));
            setProcessing(false);
            return;
        }

        const formData = new FormData();
        formData.append('cardNumber', cardNumber);
        formData.append('ghanaCardFrontPhoto', frontPhoto);
        formData.append('ghanaCardBackPhoto', backPhoto);
        formData.append('selfie', selfie);

        let url = `${STAGING_AUTH}/id-verification`;
        let headers = {
            'Authorization': `${accessToken}`,
            'Content-Type': 'multipart/form-data',
        };

        const res = await fetchRequests(url, 'POST', headers, {}, formData);
        setProcessing(false);

        if (res?.code === 401) {
            navigate('/login');
            dispatch(show({ state: true, message: 'Your session has expired, please login to continue.', type: 'token' }));
            return;
        }
        if (res?.err) {
            dispatch(show({ state: true, message: `${res.err}`, type: 'error' }));
            return;
        }

        dispatch(show({ state: true, message: `${res?.message}`, type: 'success' }));
        dispatch(updateUserInfo(res?.data?.client));
        setTimeout(() => navigate('/profile'), 200);
    };

    const isReady = isValid && frontPhoto !== '' && backPhoto !== '' && selfie !== '';

    return (
        <form onSubmit={handleSubmit} className="space-y-7">

            {/* Info callout */}
            <div className="flex items-start gap-2.5 rounded-xl bg-blue-50 dark:bg-blue-500/10 border border-blue-200 dark:border-blue-500/20 px-4 py-3">
                <HugeiconsIcon icon={InformationCircleIcon} size={16} className="text-blue-500 shrink-0 mt-0.5" />
                <p className="text-xs text-blue-700 dark:text-blue-400 leading-relaxed">
                    For best results when capturing your GhanaCard and selfie, <strong>use your mobile device</strong> in a well-lit environment.
                </p>
            </div>

            {/* Step 1 — Card number */}
            <div className="space-y-3">
                <div className="flex items-center gap-2">
                    <span className="w-5 h-5 rounded-full bg-amber-100 dark:bg-amber-500/20 flex items-center justify-center text-[10px] font-bold text-amber-700 dark:text-amber-400 shrink-0">1</span>
                    <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">GhanaCard Number</h3>
                </div>
                <div>
                    <div className="relative">
                        <input
                            ref={inputRef}
                            type="text"
                            id="ghanaCard"
                            name="ghanaCard"
                            className={`form-input font-mono pr-9 transition-colors ${
                                hasInput
                                    ? isValid
                                        ? 'border-emerald-400 focus:border-emerald-500 focus:ring-emerald-500/20'
                                        : 'border-red-300 focus:border-red-400 focus:ring-red-500/20'
                                    : ''
                            }`}
                            value={cardNumber}
                            onChange={handleCardChange}
                            onKeyDown={handleKeyDown}
                            maxLength={15}
                            spellCheck={false}
                            autoComplete="off"
                        />
                        {hasInput && (
                            <div className="absolute inset-y-0 right-3 flex items-center pointer-events-none">
                                <HugeiconsIcon
                                    icon={isValid ? CheckmarkCircle01Icon : Cancel01Icon}
                                    size={16}
                                    className={isValid ? 'text-emerald-500' : 'text-red-400'}
                                />
                            </div>
                        )}
                    </div>
                    <p className={`mt-1.5 text-xs transition-colors ${hasInput && !isValid ? 'text-red-400' : 'text-gray-400'}`}>
                        {hasInput && !isValid
                            ? 'Must be in the format GHA-123456789-0 (9 digits, then 1 digit)'
                            : 'Format: GHA-123456789-0'}
                    </p>
                </div>
            </div>

            {/* Step 2 — Photos */}
            <div className="space-y-4">
                <div className="flex items-center gap-2">
                    <span className="w-5 h-5 rounded-full bg-amber-100 dark:bg-amber-500/20 flex items-center justify-center text-[10px] font-bold text-amber-700 dark:text-amber-400 shrink-0">2</span>
                    <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">Identity Photos</h3>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <Photo label="Front of GhanaCard" name="front-photo" value={frontPhoto} setValue={setFrontPhoto} />
                    <Photo label="Back of GhanaCard"  name="back-photo"  value={backPhoto}  setValue={setBackPhoto} />
                </div>

                <Photo label="Selfie — face photo" name="selfie-photo" value={selfie} setValue={setSelfie} />
            </div>

            {/* Actions */}
            <div className="flex items-center gap-3 pt-2 border-t border-gray-100 dark:border-white/[.06]">
                <button
                    type="submit"
                    disabled={!isReady || processing}
                    className="inline-flex items-center gap-2 rounded-lg bg-amber-600 hover:bg-amber-700 px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    {processing ? (
                        <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                        </svg>
                    ) : (
                        <HugeiconsIcon icon={ShieldUserIcon} size={16} />
                    )}
                    {processing ? 'Uploading…' : 'Upload & Verify'}
                </button>

                <button
                    type="button"
                    onClick={() => navigate('/profile', { replace: true })}
                    className="rounded-lg px-5 py-2.5 text-sm font-medium text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                >
                    Cancel
                </button>
            </div>
        </form>
    );
}

export default GCardInfo;
